import axiosInstance from "@/lib/axios";
import {
  leadsEndpoints,
  lead_spesific_key,
  child_spesific_key,
} from "@/config/endpoints";
import { truncate } from "@/utils/truncate";
import { useInfiniteQuery } from "@tanstack/react-query";
import { queryClient, transientRetry } from "@/lib/queryClient";
import { useCallback, useMemo } from "react";
import { useAppSelector } from "@/stores/hook";
import type { RootState } from "@/stores/store";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import {
  cfColumnLabel,
  enabledToggleIds,
  resolvedCfIds,
} from "@/features/customField/utils/cfColumns";
import useModuleByIndustry from "@/hooks/api/useModuleByIndustry";
import useRoleCatalog from "@/features/users/api/useRoleCatalog";
import { humanizeNicheModelName } from "@/features/leads/utilities/humanizeNicheModelName";
import {
  resolveRoleColumns,
  roleShowParam,
} from "@/features/leads/utils/roleColumns";
import { sameExceptPageSize } from "@/features/leads/utils/columnFilterCoverage";

/**
 * Per-card expected toggle entries derived from Redux + catalogs. We
 * resolve labels client-side so the card can ALWAYS render a row for
 * every active toggle — even if the server response omitted it (slow
 * cache, partial data, etc.). Missing values render as "N/A".
 */
type ExpectedCardExtras = {
  cfs: Array<{ customInputId: number; label: string }>;
  modules: Array<{ nicheModelName: string; label: string }>;
  roles: Array<{ roleId: number; label: string }>;
};

/** Single source of truth for leads infinite-scroll page size. */
export const LEADS_PAGE_SIZE = 15;

/** True when API is called with revoked-only list (`isRevoked` query). */
export function isRevokedListFilter(filters?: Record<string, any>): boolean {
  const v = filters?.isRevoked;
  return v === true || v === "true" || v === 1 || v === "1";
}

export type AdaptLeadOptions = {
  /** Backend returns only user-revoked leads; mark rows hashed / view disabled. */
  markAllRevoked?: boolean;
  /** Toggle-driven extras the card MUST show — adapter materialises a
   *  placeholder row (empty values → "N/A") for any entry the server
   *  didn't include. Keeps the grid consistent column-by-column. */
  expectedExtras?: ExpectedCardExtras;
  /** Stage-route context: the server already filtered each lead's embedded
   *  `child` array to the route's stage, so the card should label the lead
   *  with the CHILD's stage it's surfaced under (which can differ from the
   *  lead's own `stageId` when the lead appears here via a child). */
  stageScoped?: boolean;
  /** With `stageScoped` on the MAIN list: the one filtered stage's id. The
   *  server may embed MORE than that stage's deals (a Verified anchor adds
   *  its representative deal), so the card labels the lead with the deal IN
   *  the filtered stage when one is embedded, else the first embedded deal. */
  routeStageId?: number | null;
  /** The single Status / Sub-status chips beside it, so among several deals
   *  in the filtered stage the card shows the one carrying every chip (two
   *  Visa deals, one Processing and one Rejected, under "Visa Rejected"). */
  routeStatusId?: number | null;
  routeSubStatusId?: number | null;
};

// ==========================================
// 1. THE ADAPTER
// ==========================================

function formatCallDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")} min`;
}

export type AggregatedPaymentStatus =
  | "full"
  | "partial"
  | "advance"
  | "pending"
  | null;

// Roll up child-level payment statuses into a single, most-progressed value
// for the parent lead card. Order: full > partial > advance > pending. Returns
// null when there are no children or no statuses to report.
function aggregatePaymentStatus(lead: any): AggregatedPaymentStatus {
  const children = Array.isArray(lead?.child) ? lead.child : [];
  if (children.length === 0) return null;

  const order: Record<string, number> = {
    full: 4,
    partial: 3,
    advance: 2,
    pending: 1,
  };

  let best: AggregatedPaymentStatus = null;
  let bestRank = 0;
  for (const c of children) {
    const s = (c?.paymentStatus || "").toLowerCase();
    const rank = order[s] ?? 0;
    if (rank > bestRank) {
      bestRank = rank;
      best = s as AggregatedPaymentStatus;
    }
  }
  return best;
}

/**
 * Returns the most recent note across the lead and all of its children,
 * shaped for the card display (`{ id, content, createdAt, ... }`). Each note
 * already comes back as the latest-per-entity from the backend, so we just
 * pick the freshest by `createdAt`.
 */
function pickLatestNote(lead: any) {
  const candidates: any[] = [];
  if (lead?.notes?.[0]) candidates.push(lead.notes[0]);

  if (Array.isArray(lead?.child)) {
    for (const c of lead.child) {
      if (c?.notes?.[0]) candidates.push(c.notes[0]);
    }
  }

  if (candidates.length === 0) {
    return { content: "" };
  }

  const latest = candidates.reduce((best, n) => {
    const a = new Date(n?.createdAt ?? 0).getTime();
    const b = new Date(best?.createdAt ?? 0).getTime();
    return a > b ? n : best;
  });

  return {
    ...latest,
    content: truncate(latest?.content || "", 60),
  };
}

export const adaptLead = (lead: any, opts?: AdaptLeadOptions) => {
  const markAllRevoked = Boolean(opts?.markAllRevoked);

  // On a stage route the server pre-filters each lead's embedded `child`
  // array to that stage (see `findWithDetails` routeStageId), so the first
  // child IS the deal this lead is surfaced under — which may differ from the
  // lead's own stage/status when the lead appears via a child (e.g. a country
  // manager seeing a deal in Application under a lead sitting in Offer Letter).
  // The card then shows THAT deal's stage + status + sub-status. Falls back to
  // the lead's own (= furthest) values for lead-anchored rows + the main list.
  const embedded: any[] = Array.isArray(lead.child) ? lead.child : [];
  const idOf = (v: any) => (v == null ? null : Number(v));
  const inRouteStage = opts?.routeStageId != null
    ? embedded.filter((c: any) => idOf(c?.stage?.id ?? c?.stageId) === Number(opts.routeStageId))
    : [];
  const carries = (c: any, statusId: number | null | undefined, subStatusId: number | null | undefined) =>
    (statusId == null || idOf(c?.status?.id ?? c?.statusId) === Number(statusId)) &&
    (subStatusId == null || idOf(c?.subStatus?.id ?? c?.subStatusId) === Number(subStatusId));
  const routeChild = opts?.stageScoped && embedded.length > 0
    ? inRouteStage.find((c: any) => carries(c, opts?.routeStatusId, opts?.routeSubStatusId)) ??
      inRouteStage.find((c: any) => carries(c, opts?.routeStatusId, null)) ??
      inRouteStage[0] ??
      embedded[0]
    : null;
  const routeChildStageName = routeChild?.stage?.name ?? null;

  // On the main list, the card should reflect the latest POSITIVE
  // (non-rejected) deal across the lead's choices — not the furthest deal when
  // that one is rejected. e.g. a lead whose Deposit deal is "Student cancelled"
  // (Rejection) but whose Pre-Application deal is still active should read as
  // Pre-Application, not Deposit·Cancelled. We pick the highest stage-order
  // child whose status is not a rejection; if none is positive (or there are
  // no deals) we fall back to the lead's own (furthest) stage/status below.
  // Best-effort over the embedded deals (capped by LEAD_LIST_CHILD_CAP, 4).
  const positiveChild = (() => {
    if (routeChild) return null; // stage routes already show the route's deal
    const kids = Array.isArray(lead.child) ? lead.child : [];
    let best: any = null;
    for (const c of kids) {
      if (!c?.status || c.status.isRejection === true) continue;
      const order = c.stage?.order ?? -Infinity;
      if (!best || order > (best.stage?.order ?? -Infinity)) best = c;
    }
    return best;
  })();

  // Status / sub-status are shown IN FULL on every card (owner, 2026-10-19:
  // "no matter what show the full status and substatus name without
  // truncate") — the card footers wrap instead. Stage keeps its 17-char label
  // with the full name on hover (FullTextTooltip).
  const stageFull =
    routeChildStageName ||
    positiveChild?.stage?.name ||
    lead.stage?.name ||
    "Select Stage";
  const statusFull =
    routeChild?.status?.name ||
    positiveChild?.status?.name ||
    lead.status?.name ||
    "Select Status";
  const subStatusFull =
    routeChild?.subStatus?.name ||
    positiveChild?.subStatus?.name ||
    lead.subStatus?.name ||
    "Select Substatus";
  return {
    id: lead.id,
    _count: lead._count,
    childCount: lead._count?.child || lead.children?.length || 0,

    // 🟢 TEXT PLACEHOLDERS (Truncated for safety)
    name: truncate(lead.name || "Unknown Lead", 25),
    program: truncate(lead.courseLevel || "No Course selected", 25),

    // Notes usually need more space, but still need a limit for the card view.
    //
    // IMPORTANT: in dynamic/child stages users add notes from the deal panel,
    // which writes Notes with `childId` set (and `leadId` null). The lead's
    // own `notes` array stays empty in that case, so we also peek into every
    // child's latest note and pick whichever note (lead or child) was created
    // most recently. Falls back to the lead note when no children exist.
    note: pickLatestNote(lead),

    phone: lead.phone, // Don't truncate phone numbers usually
    // Rendered in front of the number by MaskedPhone ("+91 98765…"). This
    // mapper WHITELISTS fields, so any new card field must be added here.
    phoneCountryCode: lead.phoneCountryCode ?? null,
    email: truncate(lead.email || "", 30),

    // 🟢 RELATIONS (Tags/Badges need strict limits)
    leadSource: truncate(lead.leadSource?.name || "Add Name", 20),
    campaign: truncate(lead.campaign?.name || "Add Campaign", 20),

    stage: truncate(stageFull, 17),
    stageFull,
    status: statusFull,
    statusFull,
    subStatus: subStatusFull,
    subStatusFull,

    branch: truncate(lead.branch?.name || "No Branch", 17),
    branchId: lead.branchId || null,
    permissionItemIds: lead.permissionItemIds ?? [],
    permissionGates: lead.permissionGates ?? [],

    // Assigned-user NAMES (string[]) — the List view's assignee column reads
    // this (the cards read the richer `assignedUsers` below). Includes BOTH
    // lead-level owners AND deal-level assignees, so the List isn't blank once
    // work moves to the deal level (same reason as `assignedUsers`).
    users: (() => {
      const names: string[] = [];
      if (lead.assignedUser?.name) {
        names.push(truncate(lead.assignedUser.name, 15));
      } else {
        for (const u of lead.LeadUsers ?? []) {
          const n = u?.user?.name;
          if (n) names.push(truncate(n, 15));
        }
      }
      // Deal-level assignees (ChildUser has no owner flag → every active row).
      for (const c of Array.isArray(lead.child) ? lead.child : []) {
        for (const cu of c?.childUsers ?? []) {
          if (cu?.isRevoked !== true && cu?.user?.name) {
            names.push(truncate(cu.user.name, 15));
          }
        }
      }
      const unique = Array.from(new Set(names));
      return unique.length > 0 ? unique : ["N/A"];
    })(),

    // Rich assigned-user objects (with role) used by the lead card to
    // render an avatar + "+N" overflow with a hover list. Kept separate
    // from `users: string[]` so legacy consumers (table, board, chat
    // sidebar, etc.) keep working. Filtered to actual owners — LeadUsers
    // also includes access-only and revoked rows that should not show as
    // "assigned to". Inactive/blocked holders are kept but flagged (below).
    //
    // Dedup by `user.id` is the last-line defense against the popover
    // showing a phantom higher count than the drawer when the join table
    // has duplicate-shaped rows (older snapshots or replication drift).
    // The drawer already does this with its `assigneeMap` keyed by
    // `user.id`; do the same here so both surfaces always agree.
    assignedUsers: (() => {
      // Lead-level owners (legacy singular `assignedUser` wins when present).
      const leadLevel = lead.assignedUser
        ? [lead.assignedUser]
        : (lead.LeadUsers?.filter(
            (u: any) => u?.isLeadOwner === true && u?.isRevoked !== true,
          ).map((u: any) => u.user).filter(Boolean) ?? []);
      // Deal-level assignees. `ChildUser` has NO owner flag, so every active
      // (non-revoked) row on a visible deal is an assignee — this is why the
      // avatar went blank once work moved to the deal level. On a stage route
      // the server already narrowed `child` to that stage, so this is the route
      // deal's holder; on the main list it unions across the lead's deals.
      const dealLevel = (Array.isArray(lead.child) ? lead.child : [])
        .flatMap((c: any) => (Array.isArray(c?.childUsers) ? c.childUsers : []))
        .filter((cu: any) => cu?.isRevoked !== true)
        .map((cu: any) => cu?.user)
        .filter(Boolean);
      const dedup = new Map<number | string, any>();
      for (const u of [...leadLevel, ...dealLevel]) {
        if (!u) continue;
        const key = u.id ?? u.name;
        if (key == null) continue;
        if (!dedup.has(key)) dedup.set(key, u);
      }
      // Deactivated/blocked holders still HOLD the lead (soft-deactivation
      // keeps assignments and the drawer lists them), so keep them — flagged,
      // and after the active ones so the first avatar is someone working it.
      const isInactive = (u: any) =>
        u?.isActive === false || u?.isBlocked === true;
      return Array.from(dedup.values())
        .sort((a, b) => Number(isInactive(a)) - Number(isInactive(b)))
        .map((u: any) => ({
          id: u?.id,
          name: u?.name || "N/A",
          avatarUrl: u?.avatar || undefined,
          role: u?.role?.name || "",
          inactive: isInactive(u),
        }));
    })(),

    // Former holders of a lead NOBODY currently holds. Server-attached only
    // for viewers allowed to see revoked assignees (org toggle + admin
    // audience) and only for leads with no live assignee — so its mere
    // presence is the signal that the card should show history instead of
    // "Unassigned". Never merged into `assignedUsers`: a card must never
    // imply that a removed counsellor is still working the lead.
    previousUsers: (lead.previousUsers ?? []).map((u: any) => ({
      id: u?.id,
      name: u?.name || "N/A",
      avatarUrl: u?.avatar || undefined,
      role: u?.role || "",
      roleId: u?.roleId ?? null,
      level: u?.level === "deal" ? "deal" : "lead",
    })),

    updatedAt: lead.updatedAt || "Not Found",
    createdAt: lead.createdAt,

    /**
     * THE LIST CLOCK — the timestamps the card corner is allowed to show.
     *
     * These are on the wire from `findWithDetails` and were simply never
     * adapted, so `pickCardSortDate` fell through to `createdAt` on EVERY
     * surface: a stage route sorted "latest moved into this stage first"
     * rendered a column of creation dates (8 Aug, 18 Sep, 1 Sep, 7 Sep…) and
     * read as unsorted; "Assignation Date" did the same.
     *
     * `cardClockAt` is the authoritative one: where the SERVER ordered the
     * page (stage routes, stage board) it ships the exact value it sorted on,
     * resolved at DEAL grain — the lead row's own clock mirrors its HIGHEST
     * deal, which on a stage route is often not the deal the card is labelled
     * with. The fields below stay as the fallback for surfaces the server
     * doesn't order (plain /leads, favorite views).
     */
    stageChange: lead.stageChange ?? null,
    statusChangedAt: lead.statusChangedAt ?? null,
    subStatusChangedAt: lead.subStatusChangedAt ?? null,
    lastAssignedAt: lead.lastAssignedAt ?? null,
    cardClockAt: lead.cardClockAt ?? null,
    cardClockLevel: lead.cardClockLevel ?? null,
    priority: lead.priority || "high",
    leadScore: lead.leadScore || 0,

    // 🔊 AUDIO — pull from real callLogs if available
    hasCallLog: (lead.callLogs?.length ?? 0) > 0,
    voiceNoteUrl: lead.callLogs?.[0]?.recordingUrl || undefined,
    voiceNoteDuration: lead.callLogs?.[0]?.duration
      ? formatCallDuration(lead.callLogs[0].duration)
      : undefined,

    avatarUrl: lead.avatarUrl || undefined,

    reenquired: lead.reenquired ?? 0,
    // Server-decided DISPLAY rule (2026-10-07): the re-enquired badge shows
    // only while nobody has acted on the lead since its latest re-enquiry.
    // The count above stays for filters/analytics. An older API (or a cached
    // page without the field) keeps the old behaviour: badge = count > 0.
    showReenquiryBadge:
      typeof lead.showReenquiryBadge === "boolean"
        ? lead.showReenquiryBadge
        : (lead.reenquired ?? 0) > 0,

    isAccessRevoked: markAllRevoked,

    // Server-authoritative: set by `lead.contact-redaction` when the backend
    // has already stripped phone/email off this row (revoked tab, non-admin
    // viewer). Drives the full-card blur/lock. Deliberately NOT derived from
    // `markAllRevoked` — tenant admins read the revoked tab as an audit
    // surface and receive unredacted rows, so only the server can say.
    isContactRedacted: lead.isContactRedacted === true,

    isArchived: lead.isArchived ?? false,
    archivedAt: lead.archivedAt ?? null,
    archivedById: lead.archivedById ?? null,

    // Next-up surfaces for list cards / view-lead header. Server returns
    // pending entries only (one of each), pre-sorted by oldest pending.
    nextFollowUp: lead.followUpDates?.[0]
      ? {
          id: lead.followUpDates[0].id,
          date: lead.followUpDates[0].date,
          status: lead.followUpDates[0].status,
        }
      : null,
    nextDeadline: lead.deadlineDates?.[0]
      ? {
          id: lead.deadlineDates[0].id,
          status: lead.deadlineDates[0].status,
          warningDays: lead.deadlineDates[0].warningDays ?? null,
          triggeredAt: lead.deadlineDates[0].triggeredAt ?? null,
          createdAt: lead.deadlineDates[0].createdAt,
          fromStatusName: lead.deadlineDates[0].fromStatus?.name ?? null,
          toStatusName: lead.deadlineDates[0].toStatus?.name ?? null,
        }
      : null,

    // Aggregated payment status across all visible children of this lead.
    // Card uses this to render a payment chip when the user enables the
    // payment-status display toggle. `null` when no children / no payments.
    paymentStatus: aggregatePaymentStatus(lead),

    // Server-attached card extras for the show/hide menu's tenant
    // custom-field and niche-module toggles. We MERGE the server data
    // with the expected toggle list so every active toggle renders a row
    // on every card — placeholders fill in as "N/A" if the server's
    // batch missed something (slow cache, racing toggle change, partial
    // data, etc.). Without this fallback an empty server array makes the
    // whole row vanish on some cards and not others, which reads as a
    // bug to the user.
    cardCustomFields: mergeCardCustomFields(
      lead.cardCustomFields,
      opts?.expectedExtras?.cfs,
    ),
    cardModules: mergeCardModules(
      lead.cardModules,
      opts?.expectedExtras?.modules,
    ),
    // Per-role assignee columns (lead- + deal-level). Same merge strategy
    // as CFs/modules: every toggled role renders a column on every card,
    // filling "N/A" when the server batch returned nothing for that lead.
    cardRoles: mergeCardRoles(lead.cardRoles, opts?.expectedExtras?.roles),
  };
};

function mergeCardCustomFields(
  fromServer: unknown,
  expected: ExpectedCardExtras["cfs"] | undefined,
): Array<{ customInputId: number; label: string; values: string[] }> {
  const serverList = Array.isArray(fromServer)
    ? (fromServer as Array<{
        customInputId: number;
        label: string;
        values: string[];
      }>)
    : [];
  if (!expected || expected.length === 0) return serverList;
  const byId = new Map(serverList.map((e) => [e.customInputId, e]));
  return expected.map((exp) => {
    const hit = byId.get(exp.customInputId);
    return {
      customInputId: exp.customInputId,
      label: hit?.label || exp.label,
      values: Array.isArray(hit?.values) ? hit!.values : [],
    };
  });
}

function mergeCardModules(
  fromServer: unknown,
  expected: ExpectedCardExtras["modules"] | undefined,
): Array<{ nicheModelName: string; label: string; values: string[] }> {
  const serverList = Array.isArray(fromServer)
    ? (fromServer as Array<{
        nicheModelName: string;
        label: string;
        values: string[];
      }>)
    : [];
  if (!expected || expected.length === 0) return serverList;
  const byName = new Map(serverList.map((e) => [e.nicheModelName, e]));
  return expected.map((exp) => {
    const hit = byName.get(exp.nicheModelName);
    return {
      nicheModelName: exp.nicheModelName,
      label: hit?.label || exp.label,
      values: Array.isArray(hit?.values) ? hit!.values : [],
    };
  });
}

function mergeCardRoles(
  fromServer: unknown,
  expected: ExpectedCardExtras["roles"] | undefined,
): Array<{ roleId: number; label: string; values: string[] }> {
  const serverList = Array.isArray(fromServer)
    ? (fromServer as Array<{
        roleId: number;
        label: string;
        values: string[];
      }>)
    : [];
  if (!expected || expected.length === 0) return serverList;
  const byId = new Map(serverList.map((e) => [e.roleId, e]));
  return expected.map((exp) => {
    const hit = byId.get(exp.roleId);
    return {
      roleId: exp.roleId,
      label: hit?.label || exp.label,
      values: Array.isArray(hit?.values) ? hit!.values : [],
    };
  });
}

export function useLeads(
  filters?: any,
  /** Rows per request. The List raises it while a column filter is on so it
   *  can check the whole list in a few requests (`useColumnFilterCoverage`). */
  { pageSize = LEADS_PAGE_SIZE }: { pageSize?: number } = {},
) {
  const markAllRevoked = useMemo(() => isRevokedListFilter(filters), [filters]);

  // Show/hide-menu toggles for tenant CFs and niche modules. Persisted via
  // redux-persist (see leadCardFunctionalitySlice). Both are passed as
  // CSV query params so the server can attach `cardCustomFields` /
  // `cardModules` to each lead row in one batched lookup per page.
  // Sorted so the toggle order doesn't fragment the React Query cache.
  const customFieldToggles = useAppSelector(
    (state: RootState) =>
      state.leadCardFunctionality.customFieldToggles ?? {},
  );
  const moduleToggles = useAppSelector(
    (state: RootState) => state.leadCardFunctionality.moduleToggles ?? {},
  );
  const roleToggles = useAppSelector(
    (state: RootState) => state.leadCardFunctionality.roleToggles ?? {},
  );
  const cfShow = useMemo(() => {
    const ids = enabledToggleIds(customFieldToggles);
    return ids.length > 0 ? ids.join(",") : "";
  }, [customFieldToggles]);
  const moduleShow = useMemo(() => {
    const names = Object.entries(moduleToggles)
      .filter(([, on]) => Boolean(on))
      .map(([name]) => name)
      .sort();
    return names.length > 0 ? names.join(",") : "";
  }, [moduleToggles]);
  // Resolve labels for every active toggle so the adapter can materialise
  // placeholder rows on cards where the server didn't return an entry.
  // Both hooks hit cached react-query data shared with the menu and the
  // detail/preference screens, so this is effectively free.
  const { spesificFields: leadCfFields } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    // Card labels are org-wide: include campaign fields so a field ticked in
    // Card Options can be labelled. Values stay per-lead.
    allCampaigns: true,
  });
  const { spesificFields: childCfFields } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    // Same reason as the leads catalog above: campaign-scoped (Meta
    // question) fields must be present or a LIVE field looks deleted to
    // `resolvedCfIds` and its column is dropped.
    allCampaigns: true,
  });
  const { modules: moduleCatalogRaw } = useModuleByIndustry();
  const { rolesPipeline, isLoading: rolesLoading } = useRoleCatalog();

  // Which roles the server attaches `cardRoles` for. The List view renders one
  // column per role — the ticked roles, or with nothing ticked every WORKING
  // role (see `resolveRoleColumns`) — so the request carries that same set.
  // The Grid / Board still show rows for TICKED roles only (`expectedExtras`
  // below); with nothing ticked the adapter passes the server list through
  // untouched, which is exactly what the List reads.
  const roleShow = useMemo(
    () => roleShowParam(resolveRoleColumns(rolesPipeline, roleToggles)),
    [rolesPipeline, roleToggles],
  );

  const expectedExtras = useMemo<ExpectedCardExtras>(() => {
    const cfLabelById = new Map<number, string>();
    for (const f of leadCfFields ?? []) {
      if (f?.id != null) cfLabelById.set(Number(f.id), getCfDisplayName(f));
    }
    for (const f of childCfFields ?? []) {
      if (f?.id != null && !cfLabelById.has(Number(f.id))) {
        cfLabelById.set(Number(f.id), getCfDisplayName(f));
      }
    }
    const cfs = resolvedCfIds(
      customFieldToggles,
      cfLabelById,
      leadCfFields != null && childCfFields != null,
    )
      .map((id) => ({
        customInputId: id,
        label: cfColumnLabel(id, cfLabelById),
      }));

    const moduleNamesInCatalog = new Set<string>();
    for (const m of (moduleCatalogRaw as any[]) ?? []) {
      const core = String(m?.coreModelName ?? "");
      if (core !== "leads" && core !== "child") continue;
      const niche = String(m?.nicheModelName ?? "");
      if (niche) moduleNamesInCatalog.add(niche);
    }
    const modulesList = Object.entries(moduleToggles)
      .filter(([, on]) => Boolean(on))
      .map(([name]) => name)
      // Only keep toggles whose niche is still in the industry — drops
      // stale Redux entries from before a tenant config change.
      .filter((n) => moduleNamesInCatalog.has(n))
      .sort()
      .map((n) => ({
        nicheModelName: n,
        label: humanizeNicheModelName(n),
      }));

    const roleLabelById = new Map<number, string>();
    for (const r of (rolesPipeline as any[]) ?? []) {
      if (r?.id != null) {
        roleLabelById.set(Number(r.id), String(r.name ?? `Role ${r.id}`));
      }
    }
    const roles = Object.entries(roleToggles)
      .filter(([, on]) => Boolean(on))
      .map(([id]) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
      // Drop stale Redux entries for roles that no longer exist.
      .filter((id) => roleLabelById.has(id))
      .sort((a, b) => a - b)
      .map((id) => ({
        roleId: id,
        label: roleLabelById.get(id) ?? `Role ${id}`,
      }));

    return { cfs, modules: modulesList, roles };
  }, [
    leadCfFields,
    childCfFields,
    moduleCatalogRaw,
    rolesPipeline,
    customFieldToggles,
    moduleToggles,
    roleToggles,
  ]);

  // Single-stage filter on the active tab → the server embeds only that
  // stage's children (routeStageId) so the card shows the matched deal's
  // stage/status, exactly like the dynamic stage grid. Multi-stage, no-stage,
  // and the revoked tab keep the lead's own stage (server sends no routeStageId
  // there, so `child[0]` isn't guaranteed to be the in-stage deal).
  const singleChip = (raw: unknown): number | null => {
    const s = String(raw ?? "").trim();
    if (!s || s.includes(",")) return null;
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const routeStageId = useMemo<number | null>(
    () => (markAllRevoked ? null : singleChip((filters as any)?.stage)),
    [filters, markAllRevoked],
  );
  const stageScoped = routeStageId != null;
  const routeStatusId = stageScoped ? singleChip((filters as any)?.status) : null;
  const routeSubStatusId = stageScoped ? singleChip((filters as any)?.subStatus) : null;

  const select = useCallback(
    (data: any) => ({
      leads: data.pages.flatMap((page: any) =>
        (page.leads || page.data || []).map((l: any) =>
          adaptLead(l, { markAllRevoked, expectedExtras, stageScoped, routeStageId, routeStatusId, routeSubStatusId })
        )
      ),
    }),
    [markAllRevoked, expectedExtras, stageScoped, routeStageId, routeStatusId, routeSubStatusId]
  );

  const {
    data,
    isLoading,
    refetch,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isPlaceholderData,
  } = useInfiniteQuery({
    // Including `cfShow` / `moduleShow` in the key (NOT in `filters`) so
    // toggling a card column triggers an immediate refetch without
    // contaminating the broader filter cache used by sibling queries.
    // A non-default page size gets its own entry; the default keeps the
    // key every other reader of this list already shares.
    queryKey: [
      "leads",
      "infinite",
      filters,
      { cfShow, moduleShow, roleShow },
      ...(pageSize !== LEADS_PAGE_SIZE ? [{ pageSize }] : []),
    ],

    // 1. Fetcher — DATA-ONLY response. The total count and basic-stats
    //    matrix moved to dedicated endpoints (`useLeadsListCount` /
    //    `useLeadsListStats`) so this query unblocks the visible cards
    //    instead of waiting on the slowest aggregate.
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams: Record<string, any> = {
        ...filters,
        page: pageParam,
        limit: pageSize,
      };
      // Only attach the params when at least one toggle is active —
      // keeps the request URL identical to today's default behaviour for
      // users who haven't enabled any card-extra columns.
      if (cfShow) queryParams.cfShow = cfShow;
      if (moduleShow) queryParams.moduleShow = moduleShow;
      if (roleShow) queryParams.roleShow = roleShow;

      const response = await axiosInstance.get(leadsEndpoints, {
        params: queryParams,
      });

      // Server now returns `{ leads, meta: { page } }` — no `total`.
      return response.data;
    },

    initialPageParam: 1,

    // 2. Next Page Logic — page-fill heuristic now that `total` is no
    //    longer returned inline. A full page (== LEADS_PAGE_SIZE rows)
    //    means there might be more; anything short is the tail. Worst
    //    case: one extra request when total is an exact multiple of the
    //    page size. Keep this aligned with LEADS_PAGE_SIZE — hardcoding
    //    breaks infinite scroll if the constant ever changes.
    getNextPageParam: (lastPage: any, allPages) => {
      const lastPageItems = (lastPage?.leads || lastPage?.data || []).length;
      if (lastPageItems === pageSize) return allPages.length + 1;
      return undefined;
    },

    // 3. Transform Data
    select,

    // Switching ONLY the page size (a column filter turned on or off) keeps
    // the rows on screen while the other size loads: without it the table
    // unmounted to a skeleton mid-click and closed the header menu. Any other
    // key change (filters, toggles) still starts from the skeleton.
    placeholderData: (previous, previousQuery) =>
      previousQuery &&
      sameExceptPageSize(previousQuery.queryKey, [
        "leads",
        "infinite",
        filters,
        { cfShow, moduleShow, roleShow },
      ])
        ? previous
        : undefined,

    // The default role columns come from the role catalog, and they ride the
    // query key (`roleShow`) — fetching before the catalog lands would fetch
    // page 1 twice (once without role data, once with). The catalog is a
    // small, cached lookup; waiting on it costs one round trip on a cold boot
    // and nothing after. `isLoading` below folds the wait in so the skeleton
    // stays up instead of flashing "No leads".
    enabled: !rolesLoading,

    // Bigger retry budget + jittered backoff for the hot leads-list path
    // so a brief 503 during a traffic spike never reaches the UI as a
    // hard error. See `transientRetry` for rationale.
    ...transientRetry,
  });

  return {
    leads: data?.leads || [],
    isLoading: isLoading || (rolesLoading && !data),
    isError,
    fetchNextPage,
    hasNextPage,
    refetch,
    error,
    isFetchingNextPage,
    isFetching,
    isPlaceholderData,
  };
}

export const refetchLeads = () => {
  queryClient.invalidateQueries({ queryKey: ["leads"] });
};
